import { useState } from "react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Upload, FileDown, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * ImportDialog — universal Excel/CSV bulk importer.
 *
 * Props:
 *  - open, onClose, onSuccess
 *  - title            : dialog title
 *  - endpoint         : POST endpoint that accepts { rows: [...] }
 *  - columns          : [{ key, label, required, type ('string'|'number') }]
 *  - sampleRow        : example row shown in template (object)
 *  - templateName     : Excel filename for template download (without .xlsx)
 */
export default function ImportDialog({
  open, onClose, onSuccess, title, endpoint, columns, sampleRow, templateName = "template",
}) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setRows([]); setFileName(""); setResult(null); };

  const downloadTemplate = () => {
    const headers = columns.map((c) => c.label + (c.required ? " *" : ""));
    const wsData = [headers, columns.map((c) => sampleRow[c.key] ?? "")];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `${templateName}.xlsx`);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
        const parsed = raw.map((r) => {
          const out = {};
          columns.forEach((c) => {
            const lbl = c.label + (c.required ? " *" : "");
            let v = r[lbl] ?? r[c.label] ?? r[c.key] ?? "";
            if (c.type === "number") v = Number(String(v).replace(/[^\d.-]/g, "")) || 0;
            else v = String(v).trim();
            out[c.key] = v;
          });
          return out;
        }).filter((r) => columns.some((c) => c.required ? r[c.key] : false));
        setRows(parsed);
        if (parsed.length === 0) toast.warning("File tidak menghasilkan baris valid — cek header sesuai template");
      } catch (err) {
        toast.error("Gagal parse file: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const submit = async () => {
    if (rows.length === 0) return toast.error("Belum ada baris untuk di-import");
    // validasi required
    for (const r of rows) {
      for (const c of columns) {
        if (c.required && !r[c.key]) return toast.error(`Field "${c.label}" wajib diisi (ada baris kosong)`);
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { rows });
      setResult(data);
      if (data.inserted > 0) toast.success(`${data.inserted} data ter-import`);
      if (data.errors?.length) toast.warning(`${data.errors.length} baris gagal — lihat detail di bawah`);
      onSuccess && onSuccess(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal import");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Upload size={18} />{title}</DialogTitle></DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-900">
            <div className="font-semibold mb-1">📋 Cara Pakai:</div>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Klik <strong>Download Template</strong> untuk mendapatkan file Excel kosong dengan header yang benar</li>
              <li>Isi baris demi baris. Kolom bertanda <strong>*</strong> wajib diisi</li>
              <li>Simpan sebagai <code>.xlsx</code> atau <code>.csv</code>, lalu klik <strong>Pilih File</strong></li>
              <li>Review preview di bawah, lalu klik <strong>Import</strong></li>
            </ol>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="outline" onClick={downloadTemplate} data-testid="import-download-template">
              <FileDown size={14} className="mr-1" /> Download Template
            </Button>
            <label className="cursor-pointer">
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
              <span className="inline-flex items-center gap-1 px-3 py-2 text-xs bg-navy-900 hover:bg-navy-800 text-white rounded-md font-medium">
                <Upload size={14} /> Pilih File Excel/CSV
              </span>
            </label>
            {fileName && <span className="text-xs text-slate-500">📄 {fileName}</span>}
            {rows.length > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{rows.length} baris siap</Badge>}
          </div>

          {rows.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-72">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-50">
                  <TableRow>{columns.map((c) => <TableHead key={c.key}>{c.label}{c.required && " *"}</TableHead>)}</TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 50).map((r, i) => (
                    <TableRow key={i}>{columns.map((c) => <TableCell key={c.key} className="text-xs">{String(r[c.key])}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length > 50 && <div className="text-[10px] text-slate-500 p-2 text-center">Menampilkan 50 baris pertama dari {rows.length}</div>}
            </div>
          )}

          {result && (
            <div className="border border-slate-200 rounded-lg p-3 space-y-1 bg-slate-50">
              <div className="flex items-center gap-2 font-semibold">
                <CheckCircle2 size={14} className="text-emerald-600" />
                <span>{result.inserted} data ter-import</span>
                {result.errors?.length > 0 && (
                  <><AlertTriangle size={14} className="text-amber-600 ml-3" /><span>{result.errors.length} gagal</span></>
                )}
              </div>
              {result.errors?.length > 0 && (
                <ul className="text-xs text-red-700 max-h-40 overflow-y-auto ml-4 list-disc">
                  {result.errors.map((er, i) => <li key={i}>Baris {er.row}: {er.error}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}><X size={14} className="mr-1" />Tutup</Button>
          <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={submit} disabled={busy || rows.length === 0} data-testid="import-submit">
            <Upload size={14} className="mr-1" />{busy ? "Mengimpor..." : `Import ${rows.length} Baris`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
